import React, { useState, useEffect, useRef } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { ChatFileAttach, uploadAttachments } from '../components/ChatFileAttach';
import ExtractionCard from '../components/ExtractionCard';
import { salesAgentAPI } from '../utils/api';
import {
  Send, Sparkles, RefreshCw, MessageSquare, Plus, Mail, UserPlus,
  Building2, FileText, CheckCircle, ChevronRight, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const QUICK_ACTIONS = [
  { label: 'Pipeline Overview', message: 'Give me a pipeline overview — how many prospects, what stages are they in?', icon: Building2 },
  { label: 'Qualify Prospect', message: 'Help me qualify a new prospect — what information do I need to collect?', icon: UserPlus },
  { label: 'Recent Emails', message: 'Show me recent inbound email prospects that were auto-created.', icon: Mail },
];

export default function SalesAgentPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [emailText, setEmailText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await salesAgentAPI.getSessions(10);
      setSessions(res.data);
    } catch (e) { console.error('loadSessions failed', e); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await salesAgentAPI.getSessionMessages(sid);
      setMessages(res.data);
      setSessionId(sid);
      setShowSessions(false);
    } catch { toast.error('Failed to load session'); }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
    setEmailMode(false);
    setProcessResult(null);
    inputRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    const hasFiles = pendingAttachments.length > 0;
    if ((!msg && !hasFiles) || loading) return;

    const userMsgPreview = msg + (hasFiles ? `\n\n📎 ${pendingAttachments.length} file(s) attached` : '');
    setMessages(prev => [...prev, { role: 'user', content: userMsgPreview, created_at: new Date().toISOString() }]);
    setInput('');
    setLoading(true);

    try {
      // Upload files first if any
      let uploadIds = [];
      if (hasFiles) {
        uploadIds = await uploadAttachments({
          attachments: pendingAttachments,
          setAttachments: setPendingAttachments,
          ownerType: 'session',
        });
      }
      const res = await salesAgentAPI.chat(msg || 'Please analyse the attached file(s)', sessionId, uploadIds.length ? uploadIds : undefined);
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        message_id: res.data.message_id,
        content: res.data.response,
        uploads: res.data.uploads,
        pending_extraction: res.data.pending_extraction,
        created_records: res.data.created_records,
        created_at: res.data.timestamp
      }]);
      setPendingAttachments([]);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Agent failed');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.', created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const processEmail = async () => {
    if (!emailText.trim() || processing) return;
    setProcessing(true);
    setProcessResult(null);
    try {
      const res = await salesAgentAPI.processEmail(emailText.trim());
      setProcessResult(res.data);
      if (res.data.status === 'created') {
        toast.success(`Prospect "${res.data.extracted?.company_name}" created!`);
      } else {
        toast.warning(res.data.message || 'Could not process email');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900" data-testid="sales-agent-title">Sales Agent</h1>
                <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">Prospect intake, qualification & proposal automation</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 overflow-x-auto">
              <Button variant="outline" size="sm" onClick={() => setShowSessions(!showSessions)} data-testid="toggle-sessions-btn" className="text-xs whitespace-nowrap">
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> History
              </Button>
              <Button variant="outline" size="sm" onClick={startNewSession} data-testid="new-session-btn" className="text-xs whitespace-nowrap">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> New Chat
              </Button>
              <Button size="sm" className={`text-xs whitespace-nowrap ${emailMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                onClick={() => { setEmailMode(!emailMode); setProcessResult(null); }} data-testid="email-mode-btn">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                {emailMode ? 'Chat' : 'Email'}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sessions Panel */}
          {showSessions && (
            <div className="w-64 border-r border-slate-200 bg-white overflow-y-auto flex-shrink-0" data-testid="sessions-panel">
              <div className="p-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recent Sessions</p>
              </div>
              {sessions.length === 0 ? (
                <p className="p-4 text-xs text-slate-400">No sessions yet</p>
              ) : (
                <div className="divide-y divide-slate-50">
                  {sessions.map((s) => (
                    <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                      className={`w-full text-left px-3 py-3 hover:bg-slate-50 transition-colors ${sessionId === s.session_id ? 'bg-amber-50 border-l-2 border-amber-500' : ''}`}>
                      <p className="text-xs text-slate-900 truncate font-medium">{s.preview || 'Session'}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{new Date(s.last_activity).toLocaleString()} · {s.message_count} msgs</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {emailMode ? (
              /* Email Processing Mode */
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="max-w-3xl mx-auto space-y-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-slate-900">Process Sales Email</h2>
                        <p className="text-xs text-slate-500">Paste an email or sales note — the agent will extract prospect details and create records automatically.</p>
                      </div>
                    </div>
                    <textarea
                      value={emailText}
                      onChange={(e) => setEmailText(e.target.value)}
                      placeholder={"Paste email here, e.g.:\n\nSpoke to ABC Hotel manager Rahul (rahul@abc.com, 9876543210). They have a 300kW load in Bangalore with 2 DGs running 6 hrs/day. Looking for BESS rental to reduce diesel costs. Budget around 8-10L/month."}
                      className="w-full h-48 border border-slate-200 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                      data-testid="email-input"
                    />
                    <div className="flex justify-end mt-3">
                      <Button onClick={processEmail} disabled={processing || !emailText.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="process-email-btn">
                        {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        {processing ? 'Processing...' : 'Extract & Create Prospect'}
                      </Button>
                    </div>
                  </div>

                  {/* Process Result */}
                  {processResult && processResult.status === 'created' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4" data-testid="process-result">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h3 className="font-semibold text-green-900">Prospect Created Successfully</h3>
                      </div>

                      {/* Extracted Data */}
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(processResult.extracted || {}).filter(([k, v]) => v && v !== 'null').map(([k, v]) => (
                          <div key={k} className="text-xs">
                            <span className="text-green-700 font-medium uppercase">{k.replace(/_/g, ' ')}: </span>
                            <span className="text-green-900">{String(v)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Created Records */}
                      <div className="flex items-center space-x-3 pt-3 border-t border-green-200">
                        {processResult.prospect && (
                          <button onClick={() => navigate(`/prospects/${processResult.prospect.prospect_id}`)}
                            className="flex items-center space-x-1 text-xs text-green-700 hover:text-green-900 bg-green-100 px-3 py-1.5 rounded" data-testid="view-prospect-btn">
                            <UserPlus className="w-3 h-3" />
                            <span>View Prospect</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                        {processResult.site && (
                          <button onClick={() => navigate(`/prospects/sites/${processResult.site.prospect_site_id}`)}
                            className="flex items-center space-x-1 text-xs text-green-700 hover:text-green-900 bg-green-100 px-3 py-1.5 rounded" data-testid="view-site-btn">
                            <Building2 className="w-3 h-3" />
                            <span>View Site</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {processResult && processResult.status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-700">{processResult.message || 'Could not extract prospect details.'}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Chat Mode */
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {messages.length === 0 && (
                    <div className="flex flex-col items-center pt-12">
                      <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                        <Sparkles className="w-8 h-8 text-amber-600" />
                      </div>
                      <h2 className="text-lg font-semibold text-slate-900 mb-1">Sales Support Agent</h2>
                      <p className="text-sm text-slate-500 mb-6 max-w-md text-center">
                        Pipeline insights, prospect qualification, and proposal automation. Use "Process Email" to auto-create prospects from email snippets.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl" data-testid="quick-actions">
                        {QUICK_ACTIONS.map((a) => (
                          <button key={a.label} onClick={() => sendMessage(a.message)}
                            className="flex items-center space-x-2 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-amber-300 hover:bg-amber-50 transition-all text-left"
                            data-testid={`quick-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                            <a.icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <span className="text-sm text-slate-700">{a.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, i) => (
                    <div key={msg.created_at ? `${msg.created_at}-${i}` : i} className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${msg.role === 'user' ? '' : 'flex-1'}`}>
                        <div className={`rounded-lg px-4 py-3 ${
                          msg.role === 'user' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-900'
                        }`} data-testid={`message-${msg.role}-${i}`}>
                          {msg.role === 'assistant' ? (
                            <div className="prose prose-sm max-w-none prose-slate"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          )}
                          <p className="text-[10px] mt-1 text-slate-400">{new Date(msg.created_at).toLocaleTimeString()}</p>
                        </div>

                        {/* Inline confirm-card for chat-side prospect creation */}
                        {msg.role === 'assistant' && msg.pending_extraction && !msg.created_records && (
                          <ExtractionCard
                            messageId={msg.message_id}
                            pending={msg.pending_extraction}
                            onResolved={(mid, createdRecords) => {
                              setMessages(prev => prev.map(m => m.message_id === mid
                                ? { ...m, pending_extraction: null, created_records: createdRecords }
                                : m));
                            }}
                            onNavigate={(path) => navigate(path)}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="mb-4 flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 text-amber-500 animate-spin" />
                        <span className="text-sm text-slate-500">Agent is thinking...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4">
                  <div className="max-w-4xl mx-auto">
                    {pendingAttachments.length > 0 && (
                      <ChatFileAttach
                        ownerType="session"
                        attachments={pendingAttachments}
                        setAttachments={setPendingAttachments}
                        disabled={loading}
                      />
                    )}
                    <div className="flex items-end space-x-2">
                      {pendingAttachments.length === 0 && (
                        <ChatFileAttach
                          ownerType="session"
                          attachments={pendingAttachments}
                          setAttachments={setPendingAttachments}
                          disabled={loading}
                        />
                      )}
                      <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                        placeholder="Ask about pipeline, paste an email, or attach a bill / DG photo..."
                        className="flex-1 resize-none border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[44px] max-h-32"
                        rows={1} disabled={loading} data-testid="agent-input" />
                      <Button onClick={() => sendMessage()} disabled={loading || (!input.trim() && pendingAttachments.length === 0)}
                        className="bg-amber-600 hover:bg-amber-700 h-[44px] px-4" data-testid="send-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-2">Powered by Claude · Press Enter to send · Attach electricity bills, DG photos, or logbooks</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
